import React, { Component } from 'react';
import { RootState } from '../../redux/store';
import { DashboardState } from '../../redux/reducers/DashboardReducer';
import { changeDashboard, setDashboardResponse } from '../../redux/actions/DashboardActions';
import { clearPage } from '../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { Dialog } from 'primereact/dialog';
import Icon from 'icons/Icon';
import FormicaLabel from '../custom/FormicaLabel';
import FormicaMultiSelectBox from '../custom/FormicaMultiSelectBox';
import AnimatedButton from '../custom/AnimatedButton';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import DashboardEmailChartSelectComponent from './DashboardEmailChartSelectComponent';
import DashboardEmailTimePickerComponent from './DashboardEmailTimePickerComponent';
import { DashboardEmailSendTime, DEFAULT_DASHBOARD_EMAIL_HOUR, DEFAULT_DASHBOARD_EMAIL_MINUTE } from './useDashboardEmailSendTime';
import { toCanonicalDashboardChartId } from '../../util/dashboardEmailChartId';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import CollectionNames from '../../constant/CollectionNames';
import RoleHelper from '../../RoleHelper';
import LanguageUtil from '../../util/LanguageUtil';

class DashboardEmailSettingsComponent extends Component<Props, State> {
  public state = new State();

  private userSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const { dialogVisible, userEmails, selectedEmails, selectedCharts } = this.state;
    const {
      dashboard: { dashboard },
    } = this.props;
    return (
      <div className={'flex w-full items-center justify-end px-5'}>
        <div
          onClick={this.showDialogVisible}
          className='cursor-pointer relative group border rounded border-[#E0E4E8] w-[28px] h-[28px] flex items-center justify-center bg-white-1 hover:bg-f-gray active:bg-[#458EF714] active:border-[#458EF7]'
        >
          <Icon icon={'settings'} className={'text-primary'} size={16} />
        </div>
        <Dialog
          header={this.getDialogHeader()}
          headerStyle={{ borderBottom: '1px solid #E0E4E8', background: '#F9FAFB', padding: 0, boxShadow: 'none' }}
          contentStyle={{ padding: '0px', height: '100%', display: 'flex' }}
          visible={dialogVisible}
          style={{ width: '50vw' }}
          onHide={this.hideDialogVisible}
        >
          <div className={'flex flex-col w-full h-full gap-y-5 px-5 py-5'}>
            <div className={'flex items-center justify-start gap-x-5'}>
              <div className={'flex flex-col w-full'}>
                <FormicaLabel label={LanguageUtil.get({ id: 'pages.dashboard.emailSettings.emails', defaultMessage: 'Emails' })} />
                <FormicaMultiSelectBox selectedItems={selectedEmails} onChange={(items) => this.onChangeSelectedMails(items)} options={userEmails} />
              </div>
            </div>
            <div className={'flex flex-col gap-y-3 w-full'}>
              <div className={'flex flex-col w-full max-w-[280px]'}>
                <FormicaLabel label={LanguageUtil.get({ id: 'pages.dashboard.emailSettings.charts', defaultMessage: 'Charts' })} />
                <DashboardEmailChartSelectComponent
                  dashboardChartIds={dashboard.charts}
                  dashboardKey={dashboard.id ?? dashboard.name}
                  selectedChartIds={selectedCharts.map((chart) => chart.value)}
                  onChange={this.onChangeSelectedCharts}
                />
              </div>
              <div className={'flex flex-col w-full max-w-[280px]'}>
                <FormicaLabel label={LanguageUtil.get({ id: 'pages.dashboard.emailSettings.periods', defaultMessage: 'Periods' })} />
                <FormicaSearchSelectBox value={dashboard.emailSettings.period.type} onChange={this.onChangePeriods} options={this.getPeriodOptions()} />
              </div>
              <div className={'flex flex-col w-full max-w-[280px]'}>
                <FormicaLabel label={LanguageUtil.get({ id: 'pages.dashboard.emailSettings.hour', defaultMessage: 'Hour' })} />
                <DashboardEmailTimePickerComponent
                  hour={dashboard.emailSettings.period.hour}
                  minute={dashboard.emailSettings.period.minute}
                  onChange={this.onChangeSendTime}
                />
              </div>
              {dashboard.emailSettings.period.type === 'custom' && (
                <div className={'flex flex-col w-full max-w-[280px]'}>
                  <FormicaLabel label={LanguageUtil.get({ id: 'pages.dashboard.emailSettings.day', defaultMessage: 'Day' })} />
                  <FormicaSearchSelectBox value={dashboard.emailSettings.period.day} onChange={this.onChangeDayOption} options={this.getDayOptions()} />
                </div>
              )}
            </div>
            <div className={'flex w-full items-center justify-end gap-x-5'}>
              <AnimatedButton
                onClick={this.onCancel}
                icon={'multiplier'}
                text={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
                customSize={'sm'}
                customType={'secondary'}
              />
              <AnimatedButton
                onClick={this.onSubmit}
                icon={'check'}
                text={LanguageUtil.get({ id: 'common.label.apply', defaultMessage: 'Apply' })}
                customSize={'sm'}
                customType={'primary'}
              />
            </div>
          </div>
        </Dialog>
      </div>
    );
  }

  public async componentDidMount() {
    this.setSelectedCharts();
    this.setSelectedEmails();
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps.dashboard.dashboard.emailSettings.emails !== this.props.dashboard.dashboard.emailSettings.emails) {
      this.setSelectedEmails();
    }
    if (prevProps.dashboard.dashboard.emailSettings.charts !== this.props.dashboard.dashboard.emailSettings.charts) {
      this.setSelectedCharts();
    }
    if (prevProps.dashboard.dashboard.charts !== this.props.dashboard.dashboard.charts) {
      this.setSelectedCharts();
    }
  }

  private tracker = async () => {
    if (this.userSubscription?.ready()) {
      const usersQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.USERS).select('email').build();
      const usersEmails: { _id: string; email: string }[] | undefined = await this.repository.findMany(usersQuery);
      const userEmailsOptions = usersEmails.map((user) => {
        return {
          label: user.email,
          value: user.email,
        };
      });

      this.setState({ userEmails: userEmailsOptions });
    }
  };

  private startTracker = async () => {
    const userEmailsQuery = query.QueryBuilder.select().from(CollectionNames.USERS);
    this.userSubscription = subscribe.Formica.subscribe(CollectionNames.USERS, userEmailsQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.userSubscription]);
  };

  private stopTracker = async () => {
    await this.userSubscription?.stop();
    this.computation?.stop();
  };

  private getPeriodOptions = (): FormicaSelectBoxItem[] => {
    return [
      {
        label: 'Daily',
        value: 'daily',
      },
      {
        label: 'Weekly',
        value: 'weekly',
      },
      {
        label: 'Monthly',
        value: 'monthly',
      },
      {
        label: 'Custom',
        value: 'custom',
      },
    ];
  };

  private setSelectedCharts = () => {
    const {
      dashboard: { dashboard },
    } = this.props;
    const dashboardChartIds = new Set(dashboard.charts);
    const selectedCharts = (dashboard.emailSettings?.charts ?? [])
      .map((chart) => toCanonicalDashboardChartId(chart))
      .filter((chartId, index, chartIds) => dashboardChartIds.has(chartId) && chartIds.indexOf(chartId) === index)
      .map((chartId) => ({
        label: chartId,
        value: chartId,
      }));
    this.setState({ selectedCharts });
  };

  private setSelectedEmails = () => {
    const {
      dashboard: { dashboard },
    } = this.props;
    const selectedEmails = dashboard.emailSettings?.emails.map((email) => {
      return {
        label: email,
        value: email,
      };
    });
    this.setState({ selectedEmails });
  };

  private showDialogVisible = () => {
    const {
      dashboard: { dashboard },
    } = this.props;
    if (!RoleHelper.canEdit('Dashboard')) return;
    if (!dashboard.name) return;
    this.setState({ dialogVisible: true });
  };

  private hideDialogVisible = () => {
    this.setState({ dialogVisible: false });
  };

  private onChangeSelectedMails(items: FormicaSelectBoxItem[]) {
    this.setState({ selectedEmails: items });
  }

  private onChangeSelectedCharts = (items: FormicaSelectBoxItem[]) => {
    this.setState({ selectedCharts: items });
  };

  private onSubmit = () => {
    const {
      dashboard: { dashboard },
      changeDashboard,
    } = this.props;
    dashboard.emailSettings.emails = this.state.selectedEmails.map((email) => email.value);
    dashboard.emailSettings.charts = this.state.selectedCharts
      .map((chart) => toCanonicalDashboardChartId(chart.value))
      .filter((chartId, index, chartIds) => dashboard.charts.includes(chartId) && chartIds.indexOf(chartId) === index);
    if (dashboard.emailSettings.period.hour === undefined) {
      dashboard.emailSettings.period.hour = DEFAULT_DASHBOARD_EMAIL_HOUR;
    }
    if (dashboard.emailSettings.period.minute === undefined) {
      dashboard.emailSettings.period.minute = DEFAULT_DASHBOARD_EMAIL_MINUTE;
    }
    changeDashboard(dashboard);
    this.hideDialogVisible();
  };

  private onCancel = () => {
    const {
      dashboard: { dashboard },
      changeDashboard,
    } = this.props;
    changeDashboard(dashboard);
    this.hideDialogVisible();
  };

  private getDialogHeader = () => {
    return (
      <div className={'flex w-full h-[52px] border-b-[1px] border-[#E0E4E8] bg-main-gray items-center px-5 gap-x-3'}>
        <Icon icon={'mail'} className={'text-primary'} size={16} />
        <p className={'text-md text-primary'}>{LanguageUtil.get({ id: 'pages.dashboard.emailSettings.title', defaultMessage: 'Send Email' })}</p>
      </div>
    );
  };

  private onChangePeriods = (e: FormicaSelectBoxItem) => {
    const {
      dashboard: { dashboard },
      changeDashboard,
    } = this.props;
    dashboard.emailSettings.period.type = e.value;
    changeDashboard(dashboard);
  };

  private getDayOptions(): FormicaSelectBoxItem[] {
    return [
      { label: 'Monday', value: 'monday' },
      { label: 'Tuesday', value: 'tuesday' },
      { label: 'Wednesday', value: 'wednesday' },
      { label: 'Thursday', value: 'thursday' },
      { label: 'Friday', value: 'friday' },
      { label: 'Saturday', value: 'saturday' },
      { label: 'Sunday', value: 'sunday' },
    ];
  }

  private onChangeDayOption = (e: FormicaSelectBoxItem) => {
    const {
      dashboard: { dashboard },
      changeDashboard,
    } = this.props;
    dashboard.emailSettings.period.day = e.value;
    changeDashboard(dashboard);
  };

  private onChangeSendTime = (time: DashboardEmailSendTime) => {
    const {
      dashboard: { dashboard },
      changeDashboard,
    } = this.props;
    dashboard.emailSettings.period.hour = time.hour;
    dashboard.emailSettings.period.minute = time.minute;
    changeDashboard(dashboard);
  };
}

interface Props extends ReduxProps {}

class State {
  dialogVisible: boolean = false;
  userEmails: FormicaSelectBoxItem[] = [];
  selectedEmails: FormicaSelectBoxItem[] = [];
  selectedCharts: FormicaSelectBoxItem[] = [];
}

const mapStateToProps = (state: RootState) => ({
  dashboard: state.dashboard as DashboardState,
});
const mapDispatchToProps = {
  changeDashboard,
  clearPage,
  setDashboardResponse,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DashboardEmailSettingsComponent);
